// composables/useMembershipStatus.js
//
// Single responsibility: surface a UI-shaped view of the membership lifecycle
// state for any page/component that needs to render or gate on it.
//
// The `membershipStatus` presenter from backend P2-A is the primary source.
// When it is absent (null — pre-P2-A cold boot or legacy localStorage), a
// `deriveLegacyKey()` fallback derives a conservative state key from the
// existing integer `membership.status` and the presence/absence of `code`.
//
// `daysRemaining` and `hoursRemaining` are recomputed live from
// `activationDeadlineAt` every 60 s while the component using this composable
// is mounted — the backend snapshot is too stale to drive a visible countdown.

import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAuthStore } from '@/stores/authStore';

/**
 * @returns {{
 *   stateKey: import('vue').ComputedRef<string|null>,
 *   tier: import('vue').ComputedRef<string|null>,
 *   accountStatus: import('vue').ComputedRef<string|null>,
 *   canDeposit: import('vue').ComputedRef<boolean>,
 *   canOrder: import('vue').ComputedRef<boolean>,
 *   code: import('vue').ComputedRef<string|null>,
 *   label: import('vue').ComputedRef<string|null>,
 *   description: import('vue').ComputedRef<string|null>,
 *   minimumDepositCents: import('vue').ComputedRef<number|null>,
 *   amountRemainingToActivateCents: import('vue').ComputedRef<number|null>,
 *   activationDeadlineAt: import('vue').ComputedRef<string|null>,
 *   daysRemaining: import('vue').ComputedRef<number|null>,
 *   hoursRemaining: import('vue').ComputedRef<number|null>,
 * }}
 */
export function useMembershipStatus() {
  const authStore = useAuthStore();
  const { membership } = storeToRefs(authStore);

  // Live clock — updated every 60 s. Drives daysRemaining / hoursRemaining
  // so the countdown doesn't freeze on mount.
  const now = ref(Date.now());
  let tickId;

  onMounted(() => {
    tickId = setInterval(() => {
      now.value = Date.now();
    }, 60_000);
  });

  onUnmounted(() => {
    clearInterval(tickId);
  });

  // ── Core state ──────────────────────────────────────────────────────────
  const stateKey = computed(() =>
    membership.value?.membershipStatus?.key ?? deriveLegacyKey(membership.value),
  );

  const tier = computed(() => membership.value?.membershipStatus?.tier ?? null);

  const accountStatus = computed(() => membership.value?.membershipStatus?.accountStatus ?? null);

  // Delegate to authStore computed (which already handles the P2-A fallback).
  const canDeposit = computed(() => authStore.canDeposit);
  const canOrder = computed(() => authStore.canOrder);

  // ── Identity ────────────────────────────────────────────────────────────
  const code = computed(() => membership.value?.code ?? null);

  // ── Presenter strings (verbatim from backend; null pre-P2-A) ───────────
  const label = computed(() => membership.value?.membershipStatus?.label ?? null);
  const description = computed(() => membership.value?.membershipStatus?.description ?? null);

  // ── Deposit activation ──────────────────────────────────────────────────
  const minimumDepositCents = computed(
    () => membership.value?.membershipStatus?.minimumDepositCents ?? null,
  );
  const amountRemainingToActivateCents = computed(
    () => membership.value?.membershipStatus?.amountRemainingToActivateCents ?? null,
  );

  // ── Deadline countdown ──────────────────────────────────────────────────
  const activationDeadlineAt = computed(
    () => membership.value?.membershipStatus?.activationDeadlineAt ?? null,
  );

  const daysRemaining = computed(() => {
    if (!activationDeadlineAt.value) return null;
    const diff = new Date(activationDeadlineAt.value).getTime() - now.value;
    return Math.max(0, Math.ceil(diff / 86_400_000));
  });

  const hoursRemaining = computed(() => {
    if (!activationDeadlineAt.value) return null;
    const diff = new Date(activationDeadlineAt.value).getTime() - now.value;
    return Math.max(0, Math.floor(diff / 3_600_000));
  });

  // ── Block date ──────────────────────────────────────────────────────────

  return {
    stateKey,
    tier,
    accountStatus,
    canDeposit,
    canOrder,
    code,
    label,
    description,
    minimumDepositCents,
    amountRemainingToActivateCents,
    activationDeadlineAt,
    daysRemaining,
    hoursRemaining,
  };
}

// ── Legacy-key derivation ──────────────────────────────────────────────────
// Used when `membershipStatus` (P2-A presenter) is absent from the membership
// object. Produces a conservative approximation of the state key so callers
// can branch on the same `stateKey` string before and after P2-A ships.
//
// Reasoning for APPROVED_FULL vs APPROVED_AWAITING_DEPOSIT:
//   A member with status=2 (APPROVED) but no `code` was approved but never
//   received a code yet — the most plausible pre-activation state is
//   AWAITING_DEPOSIT. Once `code` is present they are fully active.
//   This matches today's working signal of activation.
function deriveLegacyKey(m) {
  if (!m) return null;
  if (m.status === 1 /* PENDING_APPROVAL */)  return 'PENDING';
  if (m.status === 2 /* APPROVED */)          return m.code ? 'APPROVED_FULL' : 'APPROVED_AWAITING_DEPOSIT';
  if (m.status === 3 /* REJECTED */)          return 'REJECTED';
  if (m.status === 4 /* BLOCKED */)           return 'BLOCKED';
  return null;
}
